import React from 'react';
import { makeStyles, Button, IconButton, Icons, Paper } from '~/lib/bn-material-ui';
import { BinLink } from '~/bins/bin-link';
import { BinLevel, BinAvailableCapacity, useLatestLevelFromPageNow } from '../bin-props';
import useSirenEntity from '~/utility/use-siren-entity';
import { BinLevelImage } from '~/internal/report/tasks/outages/bin-level-image';
import '~/legacy-lit/js/bins/parts/bin-profile-index';
import { DateTime } from 'luxon';
import { getIngredientBinGraphRanges } from '~/demo/ingredient-bin-graph-ranges';

const useStyles = makeStyles(theme => ({
  container: {
    width: '20rem',
    height: '100%',
    position: 'relative',
    backgroundColor: theme.palette.gray[10],
    borderRadius: 0,
    borderRight: 'none',
    borderLeft: 'none',
    borderBottom: 'none',
  },
  binLevelImageContainer: {
    height: `calc(15rem - ${theme.spacing(1)} - ${theme.spacing(1)})`,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'stretch',
  },
  header: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.white,
    borderBottom: `1px solid ${theme.palette.gray[30]}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
  content: {
    padding: theme.spacing(1),
    height: 'calc(100% - 60px)',
    overflow: 'auto',
    display: 'flex',
    gap: '.5rem',
    flexDirection: 'column',
  },
  binLevelRow: {
    display: 'flex',
    gap: '.5rem',
  },
  binLevelColumnInner: {
    display: 'flex',
    width: '50%',
    gap: '.5rem',
    flexDirection: 'column',
  },
  binLevelColumnInnerValue: {
    padding: '.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '33.33333333333%',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  binLevelColumnInnerLabel: {
    width: '40%',
    textAlign: 'center',
    fontWeight: 'normal',
  },
  binLevelMaterial: {
    width: '100%',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.gray[80],
  },
  binProfileContainer: {
    width: '50%',
  },
  binProfile: {
    '--height-map-height': '15rem',
    margin: '-.5rem', // Accounts for baked in margin in the profile component
  },
  property: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  propertyLabel: {
    color: theme.palette.gray[60],
    fontWeight: 500,
  },
  propertyValue: {
    color: theme.palette.gray[90],
    fontWeight: 600,
  },
  closeButton: {
    color: theme.palette.gray[60],
    '&:hover': {
      color: theme.palette.gray[80],
    },
  },
}));

/**
 * Sidebar that shows details of a selected bin
 * @param {Object} props - Component props
 * @param {Object} props.selectedBin - Selected bin entity
 * @param {Object} props.selectedBinData - Selected bin data from binsForMap
 * @param {Function} props.onClose - Callback to close sidebar
 * @returns {JSX.Element} Bin detail sidebar component
 */
export default function InventoryMapBinDetailSidebar({ selectedBin, selectedBinData, onClose }) {
  const styles = useStyles();
  const { entity: latestBinLevelEntity } = useLatestLevelFromPageNow({ binHref: selectedBin?.getLinkByRel('self').href });

  // Don't render anything if no bin is selected
  if (!selectedBin) {
    return null;
  }

  const now = DateTime.utc();
  const binHref = selectedBin.getLink('self').href;

  return (
    <Paper className={styles.container} variant='outlined'>
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <BinLink binHref={binHref} variant='h6' />
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            size='small'
          >
            <Icons.Close />
          </IconButton>
        </div>
        {selectedBinData?.binType && (
          <div className={styles.headerRow}>
            <span className={styles.propertyValue}>Type: {selectedBinData.binType}</span>
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.binLevelRow}>
          <div className={styles.binProfileContainer}>
            {latestBinLevelEntity?.getSubEntityByRel('https://api.binsentry.com/rel/bin')?.href === selectedBin?.getLinkByRel('self').href && latestBinLevelEntity?.properties?.publishedAt && <bin-profile-index
              href={binHref}
              class={styles.binProfile}
              published-at={latestBinLevelEntity?.properties?.publishedAt}
            />}
          </div>

          <div className={styles.binLevelColumnInner}>
            <Paper variant='outlined' className={styles.binLevelColumnInnerValue}>
              <BinLevel binEntity={selectedBin} /> <div className={styles.binLevelColumnInnerLabel}>Current Level</div>
            </Paper>
            <Paper variant='outlined' className={styles.binLevelColumnInnerValue}>
              <div className={styles.binLevelMaterial}>
                {selectedBinData?.material || 'Unknown Material'}
              </div>
            </Paper>
            <Paper variant='outlined' className={styles.binLevelColumnInnerValue}>
              <BinAvailableCapacity binEntity={selectedBin} /> <div className={styles.binLevelColumnInnerLabel}>Open Capacity</div>
            </Paper>
          </div>
        </div>

        <Paper variant='outlined'>
          <bin-level-smooth-history-graph
            href={binHref}
            start={getIngredientBinGraphRanges().start}
            end={getIngredientBinGraphRanges().end}
            heightOverride='200px'
            content-label='inventory'
            show-inventory-periods
            time-scale='hours8'
            hide-toolbar
            hide-options
            hide-legend
            hide-zoom
          />
        </Paper>

        <Paper variant='outlined'>
          <div className={styles.binLevelImageContainer}>
            { latestBinLevelEntity && <BinLevelImage levelEntity={latestBinLevelEntity} /> }
          </div>
        </Paper>

        <div className={styles.section}>
          <Button
            variant='contained'
            color='primary'
            fullWidth
            href={`/bins/show?binId=${encodeURIComponent(binHref)}`}
          >
            View Bin
          </Button>
        </div>
      </div>
    </Paper>
  );
}
